import { Dialog } from "@mui/material";
import * as React from "react";

import { AlertDialog } from "@/shared/components";
import { AddToCollection } from "@/shared/components/collections";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type SelectionOption } from "@/shared/components/ResourceView";
import { EventRecord } from "@/shared/domain/events/EventRecord";
import { useDeleteEvents } from "@/shared/domain/events/hooks";
import {
  DeleteOutlineIcon,
  PlaylistAddIcon,
} from "@/shared/foundation/icons/mui";

/** Rows a bulk action targets, plus the callback that clears the selection on completion. */
interface PendingEventAction {
  ids: string[];
  clearSelection: () => void;
}

/**
 * The bulk actions an events list offers on its selected rows, Add to
 * Collection and Delete, for dataset and session events alike. Pass
 * `selectionOptions` to `EventsList` and render `dialogs` beside it: the
 * dialogs are where each action confirms and runs.
 *
 * @param orgId The org that owns the listed events, such as the org of the
 *   session they belong to. Collections are offered from, and deletes are made
 *   on behalf of, this org. Defaults to the current org.
 */
export function useEventSelectionActions(orgId?: string): {
  selectionOptions: SelectionOption<EventRecord>[];
  dialogs: React.ReactNode;
} {
  const { mutateAsync: deleteEvents } = useDeleteEvents(orgId);
  const showNotification = useNotification();

  const [pendingAdd, setPendingAdd] = React.useState<PendingEventAction | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] =
    React.useState<PendingEventAction | null>(null);

  const selectionOptions: SelectionOption<EventRecord>[] = React.useMemo(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          setPendingAdd({
            ids: selected.map((e) => e.event_id),
            clearSelection,
          });
        },
      },
      {
        name: "Delete",
        icon: <DeleteOutlineIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          setPendingDelete({
            ids: selected.map((e) => e.event_id),
            clearSelection,
          });
        },
      },
    ],
    [],
  );

  const handleDeleteConfirm = async () => {
    if (pendingDelete === null) {
      return;
    }
    try {
      await deleteEvents(pendingDelete.ids);
      pendingDelete.clearSelection();
    } catch {
      showNotification(
        `${pendingDelete.ids.length} event${pendingDelete.ids.length !== 1 ? "s" : ""} could not be deleted`,
        "error",
      );
    }
  };

  const dialogs = (
    <>
      <Dialog open={pendingAdd !== null} onClose={() => setPendingAdd(null)}>
        <AddToCollection
          selected={{ type: "Event", items: new Set(pendingAdd?.ids ?? []) }}
          onCancel={() => setPendingAdd(null)}
          onSuccess={() => pendingAdd?.clearSelection()}
          orgId={orgId}
        />
      </Dialog>
      <AlertDialog
        dialogOpen={pendingDelete !== null}
        handleClose={() => setPendingDelete(null)}
        dialogTitle={`Delete ${pendingDelete?.ids.length ?? 0} event${pendingDelete?.ids.length !== 1 ? "s" : ""}?`}
        dialogText="This action cannot be undone."
        dialogAction={handleDeleteConfirm}
      />
    </>
  );

  return { selectionOptions, dialogs };
}
